import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Navbar } from '../../components/Navbar';
import { API, opts, FlagChip, SOURCE_META, TierChip, customerLink } from '../../components/pointsWatch/shared';
import { WatchThresholds } from '../../components/pointsWatch/WatchThresholds';
import { WatchAuditSheet } from '../../components/pointsWatch/WatchAuditSheet';

const PERIODS = [[1, '24H'], [7, '7D'], [30, '30D'], [90, '90D']];
const chip = (on) => `px-2.5 py-1 font-mono text-[11px] border ${on ? 'border-[#39FF14] text-[#39FF14]' : 'border-[#2A0F3D] text-[#6B8E73] hover:text-[#A3C7A8]'}`;

function WatchRow({ r, onOpen }) {
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(r.user_id)} onKeyDown={(e) => e.key === 'Enter' && onOpen(r.user_id)} className="w-full text-left cursor-pointer border border-[#2A0F3D] bg-[#0C0612] hover:border-[#39FF14]/60 p-3 transition-colors" data-testid={`watch-row-${r.user_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-sm text-white truncate flex items-center gap-1.5 flex-wrap">
            <Link to={customerLink(r.user_id)} onClick={(e) => e.stopPropagation()} className="hover:text-[#00F0FF] underline decoration-[#2A0F3D] underline-offset-2" data-testid={`watch-customer-link-${r.user_id}`}>{r.name}</Link>
            <TierChip tier={r.tier} testId={`watch-tier-${r.user_id}`} />
            <span className="text-[10px] text-[#6B8E73] uppercase">{r.region}</span>
          </p>
          <p className="font-mono text-[11px] text-[#A3C7A8]">
            {Object.entries(r.by_source).map(([k, v]) => `${SOURCE_META[k]} ${v}`).join(' · ')}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-sm text-[#39FF14] font-bold" data-testid={`watch-earned-${r.user_id}`}>+{r.earned}</p>
          <p className="font-mono text-[11px] text-[#FFD700]" data-testid={`watch-pct-${r.user_id}`}>{r.pct_increase == null ? 'NEW' : `+${r.pct_increase}%`}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
        {r.flags.map((f) => <FlagChip key={f} flag={f} />)}
        <span className="font-mono text-[10px] text-[#4B6B52]">bal {r.start_balance} → {r.balance}</span>
      </div>
    </div>
  );
}

export default function PointsWatchPage() {
  const { user } = useAuth();
  const isSA = user?.role === 'super_admin';
  const [region, setRegion] = useState(isSA ? 'all' : user?.region);
  const [days, setDays] = useState(7);
  const [sort, setSort] = useState('earned');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const [openId, setOpenId] = useState(() => params.get('user'));

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(API, { ...opts, params: { region: isSA ? region : undefined, days } });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load Points Watch'); }
  }, [region, days, isSA]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let list = data?.rows || [];
    if (flaggedOnly) list = list.filter((r) => r.flags.length);
    const key = sort === 'pct' ? (r) => (r.pct_increase == null ? Infinity : r.pct_increase) : (r) => r.earned;
    return [...list].sort((a, b) => key(b) - key(a));
  }, [data, sort, flaggedOnly]);
  const flagged = (data?.rows || []).filter((r) => r.flags.some((f) => f !== 'tier_candidate')).length;

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="points-watch-page">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-5xl space-y-5">
        <Link to="/dispatch" className="inline-flex items-center gap-2 text-[#39FF14] font-mono text-sm"><ArrowLeft className="w-4 h-4" /> BACK_TO_DISPATCH</Link>
        <div>
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold flex items-center gap-3"><ShieldAlert className="w-6 h-6 text-[#FF3B30]" /> {'>'} POINTS_WATCH</h1>
          <p className="font-mono text-xs text-[#6B8E73] mt-1">// Top earners + abuse signals. Read-only — tap a customer for their full audit trail.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {isSA && ['all', 'dmv', 'nj'].map((r) => <button key={r} type="button" onClick={() => setRegion(r)} className={chip(region === r)} data-testid={`watch-region-${r}`}>{r.toUpperCase()}</button>)}
          {isSA && <span className="w-px h-5 bg-[#2A0F3D]" />}
          {PERIODS.map(([d, l]) => <button key={d} type="button" onClick={() => setDays(d)} className={chip(days === d)} data-testid={`watch-period-${d}`}>{l}</button>)}
          <span className="w-px h-5 bg-[#2A0F3D]" />
          <button type="button" onClick={() => setSort('earned')} className={chip(sort === 'earned')} data-testid="watch-sort-earned">Most pts</button>
          <button type="button" onClick={() => setSort('pct')} className={chip(sort === 'pct')} data-testid="watch-sort-pct">Biggest % jump</button>
          <button type="button" onClick={() => setFlaggedOnly(!flaggedOnly)} className={chip(flaggedOnly)} data-testid="watch-flagged-only">Flagged only</button>
          <button type="button" onClick={() => setShowLevels(!showLevels)} className={`${chip(showLevels)} inline-flex items-center gap-1`} data-testid="watch-levels-toggle"><SlidersHorizontal className="w-3 h-3" /> Flag levels</button>
        </div>
        {showLevels && (
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-4">
            <WatchThresholds region={data?.region} thresholds={data?.thresholds} onSaved={() => load()} />
          </div>
        )}
        {data && <p className="font-mono text-xs text-[#A3C7A8]" data-testid="watch-summary">{rows.length} earners · <span className="text-[#FF3B30]">{flagged} flagged</span> · last {days === 1 ? '24 hours' : `${days} days`}</p>}
        <div className="space-y-2" data-testid="watch-list">
          {!data ? <p className="font-mono text-xs text-[#6B8E73]">Loading…</p>
            : rows.length === 0 ? <p className="font-mono text-xs text-[#6B8E73]" data-testid="watch-empty">// nobody earned points in this period</p>
              : rows.map((r) => <WatchRow key={r.user_id} r={r} onOpen={setOpenId} />)}
        </div>
      </div>
      <WatchAuditSheet userId={openId} days={days} isSA={isSA} onClose={() => setOpenId(null)} />
    </div>
  );
}
